// ui/presenters/authorizationAttention.js
import { AUTHORIZATION_STATUS } from '../../domain/constants/authorization';
import { authorizationStatus, isNumberPending } from '../../domain/services/authorizationStatus';

/**
 * What a row says when something needs attention — and nothing when nothing does.
 *
 * PRESENTATION, not domain. The model keeps the two facts apart because both can be true at once;
 * collapsing them to one value is a constraint of the row, so only this presenter chooses.
 */
export const AUTHORIZATION_ATTENTION = {
  NUMBER_PENDING: 'numberPending',
  ENDED: 'ended',
  UPCOMING: 'upcoming',
};

/**
 * The one thing to show, or null when the authorization is doing its job and should stay quiet.
 *
 * ORDERED BY WHAT THE BILLER CAN DO ABOUT IT: a pending number is chaseable and blocks money, where
 * ended and upcoming are facts nobody can act on.
 *
 * WHY ENDED IS NOT FIRST, unlike `coverageAttention`. A coverage leads with ENDED because an ended
 * coverage has nothing left to bill. An ended AUTHORIZATION is not finished in that sense: services
 * delivered inside its window are still waiting to be claimed and still need the number. So a
 * missing number outlives its window, and reporting "Ended" over it would hide the row to chase.
 *
 * A list where every row says something is a list nobody reads, which is why an in-force
 * authorization with its number on file returns null rather than a reassurance.
 *
 * @param {import('../../domain/model/Authorization.types').Authorization} authorization
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {string|null} AUTHORIZATION_ATTENTION, or null when there is nothing to flag
 */
// eslint-disable-next-line import/prefer-default-export
export function authorizationAttention(authorization, asOf) {
  const status = authorizationStatus(authorization, asOf);

  if (isNumberPending(authorization)) {
    return AUTHORIZATION_ATTENTION.NUMBER_PENDING;
  }
  if (status === AUTHORIZATION_STATUS.ENDED) {
    return AUTHORIZATION_ATTENTION.ENDED;
  }
  if (status === AUTHORIZATION_STATUS.UPCOMING) {
    return AUTHORIZATION_ATTENTION.UPCOMING;
  }
  return null;
} // authorizationAttention()
